import { UserInvitationModalService } from "@gtmhub/users/components/user-invitation-form/user-invitation-modal.service";
import { IAngularEvent, IPromise, IScope } from "angular";
import { IEmployee } from "../models/models";
import { EmployeeService } from "../services/employeeService";
import { ITeamsIdMap, ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { IStateInit } from "@gtmhub/core/routing";
import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import {
  IGridFilter,
  IGridSortingRule,
  toRestLayerRequest,
  RestLayerFilterFactory,
  isPositiveOperator,
  defaultOperatorFactories,
  GridFilterDefinitions,
  GridSortingDefinitions,
  toGridDefinitionMap,
  createFilter,
  IGridFiltersChange,
  getPersistedScreenSettings,
  setPersistedScreenSettings,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { localize } from "@gtmhub/localization";
import { createRestLayerFields, IRestLayerRequest } from "@gtmhub/core";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { INgRedux } from "@gtmhub/state-management";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { ICollection } from "@gtmhub/models";
import { IUser } from "@gtmhub/users";

export interface IEmployeesScope extends IExtendedRootScopeService, IScopeFromRedux, IScope {
  employees: ICollection<IEmployee>;
  indicators: {
    employees?: IIndicator;
  };
  paginator: {
    currentPage: number;
    itemsPerPage: number;
    paginatorLimit: number;
    totalPages?: number;
    employeesTotalCount?: number;
  };
  filterDefinitions: GridFilterDefinitions;
  filters: IGridFilter[];
  sortingDefinitions: GridSortingDefinitions;
  sortingRules: IGridSortingRule[];
  onPageChange(page: number): void;
  filtersOrSortingChange(change?: IGridFiltersChange): void;
  openInvitationModal(): void;
}

const bindStateToScope = (state: ITeamsStoreState): IScopeFromRedux => {
  const { isFetched: teamsFetched, error: teamsError, map: teams } = state.teams;
  return {
    teams,
    reduxStatesInitialized: teamsFetched,
    reduxStateError: teamsError,
  };
};
interface IScopeFromRedux {
  teams: ITeamsIdMap;
  reduxStatesInitialized: boolean;
  reduxStateError: IUIError;
}

const screenPath = ["employees"];

export class EmployeesCtrl implements IStateInit {
  public static $inject = ["$rootScope", "$scope", "EmployeeService", "UIErrorHandlingService", "$ngRedux", "TeamsActions", "UserInvitationModalService"];

  constructor(
    private $rootScope: ITraceRootScopeService,
    private $scope: IEmployeesScope,
    private employeeService: EmployeeService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $ngRedux: INgRedux,
    private teamsActions: TeamsActions,
    private userInvitationModal: UserInvitationModalService
  ) {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    this.$scope.indicators = {};

    this.$scope.paginator = {
      currentPage: 1,
      itemsPerPage: 15,
      paginatorLimit: 5,
    };

    this.$scope.onPageChange = this.onPageChange;
    this.$scope.openInvitationModal = this.openInvitationModal;

    this.$scope.$on("employeesChanged", this.onEmployeesChanged);
    this.$scope.$on("userDeleted", this.onUserDeleted);
  }

  public stateInit(): IPromise<unknown> {
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());
    this.initGridFilteringBar();

    return this.init();
  }

  private init = (): IPromise<unknown> => {
    return this.$scope.watchUntilAndRejectOnError(
      () => this.$scope.reduxStatesInitialized,
      () => this.$scope.reduxStateError
    );
  };

  private initGridFilteringBar = () => {
    this.$scope.filterDefinitions = toGridDefinitionMap([
      {
        dataType: "text",
        fieldName: "name",
        displayName: localize("name_cap"),
        trackingName: "Name",
        defaultOperator: "contains",
        supportedOperators: ["equals", "notEqual", "contains", "doesNotContain"],
      },
      {
        dataType: "text",
        fieldName: "email",
        displayName: localize("email"),
        trackingName: "Email",
        defaultOperator: "contains",
        supportedOperators: ["equals", "notEqual", "contains", "doesNotContain"],
      },
      {
        dataType: "array",
        arrayType: "team",
        fieldName: "teamIds",
        displayName: localize("teams_cap"),
        trackingName: "Teams",
        defaultOperator: "contains",
        supportedOperators: ["contains"],
      },
    ]);

    this.$scope.filters = getPersistedScreenSettings(screenPath, "filters") || [
      createFilter(this.$scope.filterDefinitions["name"]),
      createFilter(this.$scope.filterDefinitions["email"]),
      createFilter(this.$scope.filterDefinitions["teamIds"]),
    ];

    this.$scope.sortingDefinitions = toGridDefinitionMap([
      {
        fieldName: "firstName",
        displayName: localize("first_name_cap"),
        trackingName: "First name",
      },
      {
        fieldName: "lastName",
        displayName: localize("last_name_cap"),
        trackingName: "Last name",
      },
    ]);

    this.$scope.sortingRules = getPersistedScreenSettings(screenPath, "sorting") || [{ fieldName: "firstName", reversed: false }];
    this.$scope.filtersOrSortingChange = this.filtersOrSortingChange;
  };

  private filtersOrSortingChange = (change?: IGridFiltersChange) => {
    if (!change || !change.initial) {
      setPersistedScreenSettings(screenPath, "filters", this.$scope.filters);
      setPersistedScreenSettings(screenPath, "sorting", this.$scope.sortingRules);
    }

    this.$scope.paginator.currentPage = 1;
    this.getEmployees();
  };

  private getEmployees = (): IPromise<unknown> => {
    this.$scope.indicators.employees = { progress: true };

    const request = this.createEmployeesRequest();
    return this.employeeService.getEmployeesV2(request).then(
      (employeesCollection) => {
        delete this.$scope.indicators.employees;

        this.$scope.employees = { items: employeesCollection.items, totalCount: employeesCollection.totalCount };
        this.$scope.paginator.totalPages = Math.ceil(employeesCollection.totalCount / this.$scope.paginator.itemsPerPage);
        this.$scope.paginator.employeesTotalCount = employeesCollection.totalCount;
      },
      (error) => {
        delete this.$scope.indicators.employees;
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  };

  private createEmployeesRequest = (): IRestLayerRequest => {
    const filtersOverride: Record<string, RestLayerFilterFactory> = {
      name: (filter) => {
        const logicalOp = isPositiveOperator(filter.operator) ? "$or" : "$and";
        const defaultFactory = defaultOperatorFactories[filter.operator];
        return { [logicalOp]: [defaultFactory({ ...filter, fieldName: "firstName" }), defaultFactory({ ...filter, fieldName: "lastName" })] };
      },
      teamIds: (filter) => {
        const defaultFactory = defaultOperatorFactories[filter.operator];
        return { ["$or"]: [defaultFactory({ ...filter, fieldName: "teamIds" }), defaultFactory({ ...filter, fieldName: "managedTeamIds" })] };
      },
    };

    const request = toRestLayerRequest({ filters: this.$scope.filters, filtersOverride, sorting: this.$scope.sortingRules });
    request.skip = (this.$scope.paginator.currentPage - 1) * this.$scope.paginator.itemsPerPage;
    request.limit = this.$scope.paginator.itemsPerPage;
    request.fields = createRestLayerFields({ id: 1, email: 1, firstName: 1, lastName: 1, picture: 1, teamIds: 1, managedTeamIds: 1, badges: 1, isActive: 1 });
    return request;
  };

  private onEmployeesChanged = (): void => {
    this.getEmployees();
  };

  private onUserDeleted = (evt: IAngularEvent, args: Pick<IUser, "id">): void => {
    // if the user is on the last page and there is more than one item -> filter the list with items without making a request
    if (this.$scope.paginator.currentPage === this.$scope.paginator.totalPages && this.$scope.employees.items.length > 1) {
      this.$scope.employees.items = this.$scope.employees.items.filter((user) => user.id !== args.id);

      return;
    }

    // if current page > 1 and there is only one item -> return the previous page, otherwise on delete item the user will be left on a blank page
    if (this.$scope.paginator.currentPage > 1 && this.$scope.employees.items.length === 1) {
      this.$scope.paginator.currentPage--;
    }

    this.onEmployeesChanged();
  };

  private onPageChange = (page: number): void => {
    this.$rootScope.traceAction("page_changed", () => {
      this.$scope.paginator.currentPage = page;
      this.getEmployees();
    });
  };

  private openInvitationModal = (): void => {
    this.userInvitationModal.open();
  };
}
